import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Chief-complaints v2 API client (api-cc-v2-service).
 *
 * Two things worth remembering when calling this:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc so callers can
 *    reconcile. Pass `0` for the first-ever write against a new patient.
 *
 * 2. Actor is derived server-side from the auth headers phr already sends
 *    (client/phone_no/auth_token). Request bodies deliberately do NOT carry
 *    an actor — sending one would be ignored anyway.
 */
class ChiefComplaintsService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add a new complaint (or reactivate a resolved one when the picked ICD
   * matches an existing entry — server enforces the dedup, no extra work here).
   *
   * Body shape must match beans.AddRequest in api-cc-v2-service:
   *   { complaint, complaintCode?, vas, sinceWhen, description?,
   *     consultedDoctor, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Update a complaint's status. VAS=0 auto-resolves. Setting VAS on an already-
   * resolved complaint is rejected 409 — the client must call `add` again with
   * the same ICD to reopen (that path preserves complaintId and trend history).
   */
  updateStatus(patientId, complaintId, body, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.updateStatus.format(patientId, complaintId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Soft-delete a complaint. Body carries `expectedVersion` so we still honour
   * optimistic locking on a DELETE (there is no query-string variant on the
   * server).
   */
  delete(patientId, complaintId, expectedVersion, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.delete.format(patientId, complaintId);
    // Our shared apiService.delete doesn't take a body — fall back to a raw
    // fetch just for this call. Keeps the rest of the codebase's contract
    // untouched.
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  trend(patientId, complaintId, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.trend.format(patientId, complaintId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Mark one history row as incorrect. Audit-only — the live Complaint
   * record is untouched. Body: { expectedVersion }.
   */
  markHistoryIncorrect(patientId, complaintId, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.markHistoryIncorrect.format(
      patientId,
      complaintId,
      index
    );
    return this.apiService.post(
      url,
      { expectedVersion: expectedVersion || 0 },
      onResponse,
      onError
    );
  }

  /**
   * Correct one history row — overwrites its display fields with the
   * supplied values; server captures the pre-correction snapshot into
   * StatusEntry.correction.previousSnapshot. Body:
   *   { vas, sinceWhen, toWhen?, description?, consultedDoctor, expectedVersion }
   */
  correctHistory(patientId, complaintId, index, body, onResponse, onError) {
    const url = APIConfig.chiefComplaintsV2.correctHistory.format(
      patientId,
      complaintId,
      index
    );
    return this.apiService.post(url, body, onResponse, onError);
  }

  // Read the current auth headers off newAPIService's shared state — done via
  // a dummy fetch call would be overkill; we reach into localStorage which
  // is where the tokens live anyway (mirrors newAPIService.initializeHeaders).
  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  // Mirror newAPIService.initializeHeaders: doctor sessions send doctor_web,
  // everyone else (employee/care manager/etc.) sends bridge_web.
  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default ChiefComplaintsService;
